import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Sparkles, ShoppingBag, Flame, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { gbp, guestBundlePct } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/context/SiteContentContext";
import { groupFlavorsByRange } from "@/lib/flavorRanges";
import { RestockNotify } from "@/components/RestockNotify";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/ef9f5075-091f-485a-984a-56151701c07c/images/14b542500c34b0dec798a4d99e77465ad41b146b04768c43e90b19869774910e.jpeg";

function tierPriceFor(tiers, qty, fallback) {
  let best = null;
  for (const t of (tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty)) {
    if (qty >= Number(t.min_qty)) best = Number(t.price);
  }
  return best != null ? best : fallback;
}

function tierLabel(tiers) {
  const sorted = (tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
  return sorted.map((t, i) => {
    const next = sorted[i + 1];
    const range = next ? (next.min_qty - t.min_qty === 1 ? `${t.min_qty}` : `${t.min_qty}\u2013${next.min_qty - 1}`) : `${t.min_qty}+`;
    return { range, price: Number(t.price) };
  });
}

function GuestCard({ flavor }) {
  const { addItem } = useCart();
  const { user } = useAuth();
  const isTrade = user?.role === "dropship";
  const retailTiers = useMemo(() => (Array.isArray(flavor.guest_retail_tiers) ? flavor.guest_retail_tiers : []), [flavor.guest_retail_tiers]);
  const tradeTiers = useMemo(() => (Array.isArray(flavor.guest_trade_tiers) ? flavor.guest_trade_tiers : []), [flavor.guest_trade_tiers]);
  const premPct = isTrade ? Number(user?.premium_discount_pct || 0) : 0;
  const basePrice = Number(flavor.guest_price || 0);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({}); // label -> true
  const options = useMemo(() => (Array.isArray(flavor.guest_options) ? flavor.guest_options : []), [flavor.guest_options]);

  const optionPrice = useCallback((o) => (isTrade && o.trade_price != null && o.trade_price !== "" ? Number(o.trade_price) : Number(o.price)) || 0, [isTrade]);
  const optionList = useMemo(
    () => options.filter((o) => selected[o.label]).map((o) => ({ label: o.label, price: optionPrice(o) })),
    [options, selected, optionPrice]
  );
  const optionsTotal = useMemo(() => optionList.reduce((s, o) => s + o.price, 0), [optionList]);

  // Ladder this customer actually pays from (trade ladder if set, else retail).
  const ladder = useMemo(() => (isTrade && tradeTiers.length ? tradeTiers : retailTiers), [isTrade, tradeTiers, retailTiers]);

  // Per-bottle base at the currently selected quantity (mirrors backend priority).
  const perBottleBase = useMemo(() => {
    if (isTrade) {
      const ov = (user?.premium_overrides || {})[flavor.id];
      if (ov != null && ov !== "") return Number(ov);
      if (tradeTiers.length) return tierPriceFor(tradeTiers, qty, tierPriceFor(retailTiers, qty, basePrice));
      const retail = tierPriceFor(retailTiers, qty, basePrice);
      return Math.round(retail * (1 - premPct / 100) * 100) / 100;
    }
    return tierPriceFor(retailTiers, qty, basePrice);
  }, [isTrade, tradeTiers, retailTiers, qty, basePrice, premPct, user, flavor.id]);

  const retailPerBottle = tierPriceFor(retailTiers, qty, basePrice);
  const unit = Math.round((perBottleBase + optionsTotal) * 100) / 100;
  const dispPrice = Math.round(perBottleBase * 100) / 100;
  const savePct = isTrade && retailPerBottle > perBottleBase && retailPerBottle > 0
    ? Math.round((1 - perBottleBase / retailPerBottle) * 100) : 0;
  const chips = useMemo(() => tierLabel(ladder), [ladder]);

  const add = () => {
    const optKey = optionList.map((o) => o.label).sort().join("|");
    addItem({
      type: "guest",
      key: `guest:${flavor.id}:${optKey}`,
      flavor_id: flavor.id,
      flavor_name: flavor.name,
      brand_name: flavor.brand_name || "",
      size_label: flavor.guest_size_label || "",
      guest_price: dispPrice,
      options: optionList,
      unit_price: unit,
      quantity: qty,
      accent_color: flavor.accent_color,
      image_url: flavor.image_url,
    });
    toast.success(`${qty} × ${flavor.name} added`);
    setQty(1);
    setSelected({});
  };

  return (
    <div className="group relative flex h-full flex-col overflow-hidden border border-border bg-card transition-colors hover:border-primary" data-testid={`guest-card-${flavor.id}`}>
      <div className="relative h-56 w-full overflow-hidden bg-white">
        {flavor.image_url ? (
          <img src={flavor.image_url} alt={flavor.name} className="h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center" style={{ background: `linear-gradient(135deg, ${flavor.accent_color || "#7c3aed"}, #0a0a0a)` }}>
            <Sparkles className="h-9 w-9 text-white/80" />
          </div>
        )}
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 bg-black/70 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur">
          <Flame className="h-3 w-3 text-primary" /> Limited
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        {flavor.brand_name && (
          <div className="mono text-[10px] font-bold uppercase tracking-[0.2em] text-primary">{flavor.brand_name}</div>
        )}
        <div className="mt-1 font-heading text-xl font-black leading-tight">{flavor.name}</div>
        <div className="mono mt-1 text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
          {flavor.guest_size_label || "Fixed size"} · {savePct > 0 ? (
            <><span className="text-primary font-bold">{gbp(dispPrice)}</span> <span className="line-through opacity-60">{gbp(retailPerBottle)}</span> <span className="text-primary">· save {savePct}%</span></>
          ) : gbp(dispPrice)}
        </div>
        {chips.length > 1 && (
          <div className="mt-2" data-testid={`guest-tiers-${flavor.id}`}>
            <div className="flex flex-wrap gap-1.5">
              {chips.map((c) => {
                const active = Number(c.price) === Number(perBottleBase);
                return (
                  <span key={c.range} className={`mono border px-1.5 py-0.5 text-[10px] ${active ? "border-primary bg-primary/15 text-primary font-bold" : "border-border text-muted-foreground"}`}>
                    {c.range}: {gbp(c.price)}
                  </span>
                );
              })}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">Price drops with the total Premium bottles in your basket — applied at checkout.</p>
          </div>
        )}
        {flavor.description && <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{flavor.description}</p>}

        {options.length > 0 && (
          <div className="mt-4 space-y-2">
            {options.map((o) => (
              <label key={o.label} className="flex cursor-pointer items-center justify-between gap-2 border border-border px-3 py-2 text-sm transition-colors hover:border-primary">
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid={`guest-option-${flavor.id}-${o.label}`}
                    checked={!!selected[o.label]}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [o.label]: e.target.checked }))}
                  />
                  {o.label}
                </span>
                <span className="mono text-xs text-primary">+{gbp(optionPrice(o))}{isTrade && o.trade_price != null && o.trade_price !== "" && Number(o.trade_price) < Number(o.price) ? <span className="ml-1.5 text-muted-foreground line-through">{gbp(o.price)}</span> : null}</span>
              </label>
            ))}
          </div>
        )}

        <div className="mt-auto pt-4">
          {flavor.stock <= 0 ? (
            <RestockNotify flavorId={flavor.id} compact />
          ) : (
          <>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center border border-border">
              <button data-testid={`guest-minus-${flavor.id}`} onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2 hover:bg-muted"><Minus className="h-3.5 w-3.5" /></button>
              <span className="w-10 text-center mono text-sm">{qty}</span>
              <button data-testid={`guest-plus-${flavor.id}`} onClick={() => setQty((q) => q + 1)} className="p-2 hover:bg-muted"><Plus className="h-3.5 w-3.5" /></button>
            </div>
            <div className="mono text-sm font-bold">{gbp(unit * qty)}</div>
          </div>
          <button
            data-testid={`guest-add-${flavor.id}`}
            onClick={add}
            className="mono mt-3 flex w-full items-center justify-center gap-2 bg-primary py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-primary-foreground transition-transform hover:-translate-y-0.5"
          >
            <ShoppingBag className="h-4 w-4" /> Add to cart
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GuestCollectionBuilder() {
  const cats = useCategories();
  const [flavors, setFlavors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/flavors")
      .then((r) => setFlavors((r.data || []).filter((f) => f.range_type === "guest" && Number(f.guest_price) > 0)))
      .catch(() => setFlavors([]))
      .finally(() => setLoaded(true));
  }, []);

  const allGuest = useMemo(() => {
    const groups = groupFlavorsByRange(flavors).guestGroups;
    return groups.flatMap((g) => g.flavors);
  }, [flavors]);

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-10" data-testid="guest-collection-page">
      {/* Compact header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative overflow-hidden border border-border"
        data-testid="guest-hero"
      >
        <img src={HERO_IMAGE} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/45" />
        <div className="relative flex flex-wrap items-center justify-between gap-4 px-6 py-5 sm:px-8">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 border border-primary/50 bg-primary/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-primary" data-testid="guest-hero-badge">
              <Flame className="h-3 w-3" /> Limited Drop
            </span>
            <h1 className="mt-2 font-heading text-2xl font-black tracking-tighter sm:text-3xl">
              {cats.guest.title}
            </h1>
            <p className="mt-1 max-w-lg text-sm text-muted-foreground">
              Rare premium brands, lab-crafted in small runs — here today, gone when they sell out.
            </p>
          </div>
          {guestBundlePct() > 0 && (
            <Link to="/build-your-box" data-testid="guest-bundle-banner" className="inline-flex shrink-0 items-center gap-2 border border-primary/50 bg-primary/15 px-3 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/25">
              <Zap className="h-3.5 w-3.5" /> Bundle &amp; save {guestBundlePct()}% with a {cats.build.title}
            </Link>
          )}
        </div>
      </motion.div>

      {loaded && flavors.length === 0 && (
        <div className="mt-10 border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="guest-empty">
          No premium brand products available right now — check back soon.
        </div>
      )}

      {/* All premium products, side by side */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="guest-grid">
        {allGuest.map((f, i) => (
          <motion.div
            key={f.id}
            className="h-full"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
          >
            <GuestCard flavor={f} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
